import { DateTime, DateTimeUnit } from 'luxon';
import { TimeZone } from './timeZoneEnum';

export const defaultFullDateTimeFormat = 'dd-MM-yyyy HH:mm:ss';
export const defaultTimeFormat = 'HH:mm';
export const defaultDateMonthFormat = 'MM-yyyy';
export const defaultDateFormat = 'dd-MM-yyyy';
export const defaultDateTimeFormat = 'dd-MM-yyyy HH:mm';

export const defaultAPIDateFormat = 'yyyy-MM-dd';
export const defaultAPIDateTimeFormat = 'yyyy-MM-dd HH:mm';

export const checkWrongDate = (date?: DateTime) => {
    return !date || date === undefined || date === null || !date.isValid;
};

export function dateToApiDateStringFormat(date: DateTime | undefined): string {
    if (!date || !date.isValid || date.toFormat(defaultDateFormat) === '01-01-0001') {
        return '';
    }

    return date.toFormat(defaultAPIDateFormat);
}

export function createDateFromTimeZone(
    input: string | Date | undefined | null,
    timeZone: TimeZone = TimeZone.Europe_Warsaw,
    format?: 'month' | 'dateTime' | 'utc',
): DateTime {
    if (input === undefined || input === null) {
        return DateTime.fromISO('');
    } else if (typeof input === 'string') {
        switch (format) {
            case 'utc':
                return DateTime.fromISO(input as string).setZone(timeZone);
            case 'month':
                return DateTime.fromFormat(input as string, defaultDateMonthFormat).setZone(timeZone);
            case 'dateTime':
                return DateTime.fromFormat(input as string, defaultDateTimeFormat).setZone(timeZone);
            default:
                return DateTime.fromFormat(input as string, defaultDateFormat).setZone(timeZone);
        }
    } else {
        return DateTime.fromJSDate(input as Date).setZone(timeZone);
    }
}

export function dateToStringFormat(
    date: DateTime | undefined,
    timeZone: TimeZone = TimeZone.Europe_Warsaw,
    keepLocalTime?: boolean,
): string {
    let currentDate = typeof date === 'string' && date !== '' ? DateTime.fromFormat(date, defaultDateFormat) : date;
    if (!currentDate || !currentDate.isValid || currentDate.toFormat(defaultDateFormat) === '01-01-0001') {
        return '';
    }

    return currentDate.setZone(timeZone, { keepLocalTime }).toFormat(defaultDateFormat);
}

export function dateToMonthFormat(date: DateTime | undefined, timeZone: TimeZone = TimeZone.Europe_Warsaw): string {
    if (!date || !date.isValid || date.toFormat(defaultDateMonthFormat) === '01-0001') {
        return '';
    }

    return date.setZone(timeZone).toFormat(defaultDateMonthFormat);
}

export const dateToDateTimeFormat = (
    date: DateTime | undefined,
    timeZone: TimeZone | string = TimeZone.Europe_Warsaw,
): string => {
    if (!date || !date.isValid || date.toFormat(defaultDateTimeFormat) === '01-01-0001 00:00') {
        return '';
    }
    return date.setZone(timeZone).toFormat(defaultDateTimeFormat);
};

export const dateToFullDateTimeFormat = (
    date: DateTime | undefined,
    timeZone: TimeZone | string = TimeZone.Europe_Warsaw,
): string => {
    if (!date || !date.isValid || date.toFormat(defaultFullDateTimeFormat) === '01-01-0001 00:00:00') {
        return '';
    }
    return date.setZone(timeZone).toFormat(defaultFullDateTimeFormat);
};

export const dateToTimeFormat = (date: DateTime | undefined, timeZone: TimeZone = TimeZone.Europe_Warsaw): string => {
    if (!date || !date.isValid) {
        return '';
    }
    return date.setZone(timeZone).toFormat(defaultTimeFormat);
};

export const isIntersection = (
    period1From: DateTime,
    period1To: DateTime | undefined,
    period2From: DateTime,
    period2To: DateTime | undefined,
    granularity?: DateTimeUnit,
) => {
    if (
        ((!period1From || checkWrongDate(period1From)) && (!period1To || checkWrongDate(period1To))) ||
        ((!period2From || checkWrongDate(period2From)) && (!period2To || checkWrongDate(period2To)))
    ) {
        return false;
    }

    if (granularity) {
        return (
            ((checkWrongDate(period1From) || period1From.startOf(granularity) <= period2From.startOf(granularity)) &&
                (!period1To ||
                    checkWrongDate(period1To) ||
                    period1To.startOf(granularity) >= period2From.startOf(granularity))) ||
            ((checkWrongDate(period2From) || period1From.startOf(granularity) >= period2From.startOf(granularity)) &&
                (!period2To || checkWrongDate(period2To) || period1From.startOf(granularity) <= period2To.startOf(granularity)))
        );
    } else {
        return (
            ((checkWrongDate(period1From) || period1From <= period2From) &&
                (!period1To || checkWrongDate(period1To) || period1To >= period2From)) ||
            ((checkWrongDate(period2From) || period1From >= period2From) &&
                (!period2To || checkWrongDate(period2To) || period1From <= period2To))
        );
    }
};

export const formatToDateOffset = (date: DateTime | undefined, timeZone: TimeZone = TimeZone.Europe_Warsaw) => {
    return date?.setZone(timeZone).toFormat(defaultAPIDateFormat) as any;
};

export function stringAsTimeZone(timeZone: string): TimeZone {
    if (!Object.values<string>(TimeZone).includes(timeZone)) {
        console.error(`"${timeZone}" is not recognized as valid TimeZone`);
        return TimeZone.UTC;
    }

    return timeZone as TimeZone;
}

export const getDefaultTimeZone = () => TimeZone.Europe_Warsaw;
